import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Spinner } from "react-bootstrap";
import { Form, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faCheck,
  faArrowLeft,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { TailSpin } from "react-loader-spinner";
import { useToasts } from "../contexts/toasts";
import { Users } from "../api/Users";
import "./Login.css";
import logo from "../assets/img/LOGO CLAUDIO MESSINA_MONOGRAMMA PNG_MONOGRAMMA COLORE-05.png";

export default function ReIssuePassword() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    Users.getUserByTokenReIssue(token).then(
      () => setTokenValid(true),
      (err) => {
        setTokenValid(false);
        const msg =
          err?.response?.data?.message ||
          "C'è stato un errore, riprova o contatta il trainer";
        setErrorMessage(msg);
      }
    );
  }, [token, navigate]);

  const handleSubmit = useCallback(
    (values: any) => {
      if (!token) return;
      setLoading(true);
      Users.resetPassword({
        token,
        oldPassword: values.oldPassword,
        password: values.password,
        passwordConfirmation: values.passwordConfirmation,
      }).then(
        () => {
          setLoading(false);
          showToast(
            "verify-success",
            "Password aggiornata con successo. Adesso puoi effettuare il login",
            "",
            "success"
          );
          navigate("/login");
        },
        (err) => {
          setLoading(false);
          const msg =
            err?.response?.data?.message || "Errore nel reset della password";
          showToast("verify-error", msg, "", "danger");
        }
      );
    },
    [token, navigate, showToast]
  );

  if (tokenValid === null) {
    return (
      <div className="login-container">
        <Card className="login-card">
          <Card.Body>
            <div className="login-hero">
              <span className="login-hero-logo">
                <img src={logo} alt="Coaching Master" />
              </span>
              <h1 className="login-hero-title">Coaching Master</h1>
              <span className="login-hero-subtitle">
                Stiamo verificando il link...
              </span>
            </div>
            <div className="d-flex justify-content-center py-4">
              <TailSpin height={64} width={64} color="#9c1419" radius="1" />
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="login-container">
        <Card className="login-card">
          <Card.Body>
            <div className="login-hero">
              <span className="login-hero-logo">
                <img src={logo} alt="Coaching Master" />
              </span>
              <h1 className="login-hero-title">Link non valido</h1>
              <span className="login-hero-subtitle">
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="me-2"
                  style={{ color: "#faad14" }}
                />
                {errorMessage}
              </span>
            </div>
            <button
              type="button"
              className="button-primary login-submit"
              onClick={() => navigate("/login")}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span className="ms-2">Torna al login</span>
            </button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="login-container">
      <Card className="login-card">
        <Card.Body>
          <div className="login-hero">
            <span className="login-hero-logo">
              <img src={logo} alt="Coaching Master" />
            </span>
            <h1 className="login-hero-title">Reimposta la password</h1>
            <span className="login-hero-subtitle">
              Inserisci la password temporanea ricevuta via email e scegline una
              nuova
            </span>
          </div>

          <Form
            className="login-form"
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
          >
            <Form.Item
              name="oldPassword"
              label="Password temporanea"
              rules={[
                {
                  required: true,
                  message: "La password temporanea è obbligatoria",
                },
              ]}
            >
              <Input.Password
                size="large"
                placeholder="Password temporanea..."
                autoComplete="one-time-code"
                iconRender={(visible) => (
                  <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
                )}
              />
            </Form.Item>
            <Form.Item
              name="password"
              label="Nuova password"
              hasFeedback
              rules={[
                {
                  required: true,
                  message: "La nuova password è obbligatoria",
                },
                {
                  min: 6,
                  message: "La password deve contenere almeno 6 caratteri",
                },
              ]}
            >
              <Input.Password
                placeholder="Nuova password..."
                size="large"
                autoComplete="new-password"
                iconRender={(visible) => (
                  <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
                )}
              />
            </Form.Item>
            <Form.Item
              name="passwordConfirmation"
              label="Conferma password"
              dependencies={["password"]}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: "Devi confermare la nuova password",
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Le password non coincidono")
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                placeholder="Conferma password..."
                size="large"
                autoComplete="new-password"
                iconRender={(visible) => (
                  <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
                )}
              />
            </Form.Item>

            <button
              type="submit"
              disabled={loading}
              className={classNames("button-primary login-submit", {
                "button-disabled": loading,
              })}
            >
              {loading ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} />
                  <span className="ms-2">Conferma</span>
                </>
              )}
            </button>

            <div className="login-footer">
              <button
                type="button"
                className="btn btn-link p-0 login-footer-link"
                onClick={() => navigate("/login")}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Torna al login
              </button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}
